'use client';

import { type FC } from 'react';
import { ScreenContent } from '../../../screen';
import { Button } from '../../ui';
import {
  WEAPON_ICON_SRC, GRENADE_ICON_SRC, SMOKE_ICON_SRC, FLASH_ICON_SRC, KNIFE_ICON_SRC, MEDKIT_ICON_SRC,
} from '../spriteLayout';
import { bustCache } from '../render/battleAssetVersion';
import { useStoredImageSrc } from '../../../lib/useStoredAsset';

export const LOADING_ART_URL = bustCache('/battle/rush_mid_loading.webp');

const SCATTER: { src: string; top: string; left: string; size: number; dur: number; delay: number }[] = [
  { src: WEAPON_ICON_SRC.awp, top: '6%', left: '8%', size: 74, dur: 6.0, delay: 0 },
  { src: KNIFE_ICON_SRC, top: '4%', left: '48%', size: 44, dur: 5.6, delay: 1.2 },
  { src: GRENADE_ICON_SRC, top: '13%', left: '74%', size: 40, dur: 5.2, delay: 0.6 },
  { src: WEAPON_ICON_SRC.ak, top: '24%', left: '22%', size: 60, dur: 6.4, delay: 0.3 },
  { src: SMOKE_ICON_SRC, top: '30%', left: '88%', size: 38, dur: 4.8, delay: 0.9 },
  { src: WEAPON_ICON_SRC.lmg, top: '42%', left: '4%', size: 66, dur: 6.8, delay: 0.2 },
  { src: FLASH_ICON_SRC, top: '54%', left: '90%', size: 40, dur: 5.0, delay: 1.5 },
  { src: MEDKIT_ICON_SRC, top: '64%', left: '14%', size: 42, dur: 5.4, delay: 0.7 },
  { src: WEAPON_ICON_SRC.m4, top: '70%', left: '78%', size: 58, dur: 6.2, delay: 0.4 },
  { src: GRENADE_ICON_SRC, top: '85%', left: '30%', size: 38, dur: 4.9, delay: 1.1 },
  { src: WEAPON_ICON_SRC.awp, top: '88%', left: '66%', size: 70, dur: 6.6, delay: 0.5 },
  { src: KNIFE_ICON_SRC, top: '78%', left: '92%', size: 40, dur: 5.3, delay: 0.1 },
  { src: WEAPON_ICON_SRC.ak, top: '92%', left: '6%', size: 52, dur: 6.1, delay: 0.8 },
  { src: MEDKIT_ICON_SRC, top: '18%', left: '38%', size: 34, dur: 4.7, delay: 0.5 },
  { src: FLASH_ICON_SRC, top: '9%', left: '90%', size: 36, dur: 5.9, delay: 1.0 },
  { src: WEAPON_ICON_SRC.m4, top: '34%', left: '62%', size: 50, dur: 6.5, delay: 0.2 },
  { src: KNIFE_ICON_SRC, top: '58%', left: '44%', size: 36, dur: 5.4, delay: 2.0 },
  { src: WEAPON_ICON_SRC.lmg, top: '72%', left: '50%', size: 54, dur: 6.9, delay: 0.9 },
  { src: GRENADE_ICON_SRC, top: '50%', left: '30%', size: 34, dur: 4.6, delay: 1.4 },
  { src: WEAPON_ICON_SRC.ak, top: '60%', left: '70%', size: 52, dur: 6.3, delay: 0.6 },
  { src: SMOKE_ICON_SRC, top: '82%', left: '52%', size: 34, dur: 5.0, delay: 0.3 },
  { src: WEAPON_ICON_SRC.awp, top: '38%', left: '36%', size: 58, dur: 6.7, delay: 1.3 },
  { src: FLASH_ICON_SRC, top: '26%', left: '6%', size: 36, dur: 4.9, delay: 0.7 },
  { src: MEDKIT_ICON_SRC, top: '94%', left: '82%', size: 38, dur: 5.5, delay: 1.6 },
  { src: SMOKE_ICON_SRC, top: '46%', left: '54%', size: 32, dur: 5.1, delay: 1.8 },
];

export const RushMidLoadingScreen: FC<{
  phrase: string;
  pct: number;
  error?: string | null;
  onReload?: () => void;
}> = ({ phrase, pct, error, onReload }) => {
  const art = useStoredImageSrc(LOADING_ART_URL);
  return (
  <ScreenContent hideLegalSection scrollKey="rush-mid-asset-loading">

    <div className="absolute inset-0 overflow-hidden bg-black">
      {art && (
        <img
          src={art}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full scale-125 object-cover blur-3xl brightness-[0.55]"
        />
      )}

      <div className="pointer-events-none absolute inset-0 z-[1]" aria-hidden>
        {SCATTER.map((s, i) => (
          <img
            key={i}
            src={bustCache(s.src)}
            alt=""
            draggable={false}
            className="absolute animate-icon-sway select-none object-contain opacity-[0.1]"
            style={{ top: s.top, left: s.left, width: s.size, height: s.size, animationDuration: `${s.dur}s`, animationDelay: `${s.delay}s` }}
          />
        ))}
      </div>
      <div className="absolute inset-0 z-[2] flex items-center justify-center">
        {art && (
          <img
            src={art}
            alt=""
            className="h-[28rem] w-[28rem] max-w-[85vw] animate-shot-logo-pulse object-contain drop-shadow-[0_12px_40px_rgba(0,0,0,0.7)]"
          />
        )}
      </div>

      <div className="absolute inset-x-0 bottom-0 z-[3] flex flex-col items-center gap-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-6 pt-16 pb-[max(72px,calc(var(--tg-viewport-safe-area-inset-bottom,0px)+64px))] text-center">
        <div className="text-base font-semibold text-white drop-shadow transition-opacity duration-300">{phrase}</div>
        {!error ? (
          <div className="flex w-full flex-col items-center gap-1.5">
            <div className="h-2 w-full overflow-hidden rounded-full bg-white/15">
              <div
                className="h-full rounded-full bg-accent-blue"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="text-xs tabular-nums text-white/70">{pct}%</div>
          </div>
        ) : (
          <>
            <div className="text-sm text-white/80">{error}</div>
            <Button variant="tertiary" size="l" onClick={onReload ?? (() => window.location.reload())}>
              Обновить экран
            </Button>
          </>
        )}
      </div>
    </div>
  </ScreenContent>
  );
};

export default RushMidLoadingScreen;
